/**
 * Applying the theme preference (docs/04 §1b, ADR-64).
 *
 * The stored preference is one of three values and the DOM has two states, so
 * the mapping is deliberate:
 *
 *   'system' → no `data-theme` attribute. The un-stamped document is what
 *              tokens.css's `@media (prefers-color-scheme: dark)` block reads,
 *              so following the device costs no JavaScript at all.
 *   'light'  → data-theme="light", which beats a dark OS (the media block is
 *              guarded `:root:not([data-theme='light'])`).
 *   'dark'   → data-theme="dark", which beats a light OS.
 *
 * Removing the attribute is therefore not "unset" — it IS the system state, and
 * that is why applyTheme removes rather than stamps "system".
 */

import { themeSchema, type ThemePreference } from '@propos/shared';

/**
 * A cache, never the store. The preference lives on the server (docs/04 §5) and
 * arrives one round-trip after boot; a person who chose Light on a dark laptop
 * would otherwise watch the app load dark and then flip. So the last applied
 * value is mirrored here and replayed synchronously before React mounts. If it
 * is stale or absent the server's answer corrects it within the same second,
 * and a browser that refuses storage entirely just starts on the device setting.
 */
const CACHE_KEY = 'propos.theme';

/**
 * The mobile browser chrome, read back off the document rather than listed here
 * as a pair of hex values: --canvas is the token that decides what the page's
 * ground looks like, and a second copy of it in TypeScript is a copy that goes
 * stale. Read after the stamp, so the value is the new theme's — computed
 * custom properties honour both the attribute and prefers-color-scheme.
 */
function syncChrome(): void {
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta === null) return;
  const canvas = getComputedStyle(document.documentElement).getPropertyValue('--canvas').trim();
  if (canvas !== '') meta.setAttribute('content', canvas);
}

/**
 * The DOM half, without the cache. Separate so the design gallery can preview a
 * theme for the life of that page without silently rewriting what the next boot
 * replays — a designer flipping the gallery to dark has not changed their
 * preference.
 */
export function stampTheme(theme: ThemePreference): void {
  const root = document.documentElement;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);
  syncChrome();
}

export function applyTheme(theme: ThemePreference): void {
  stampTheme(theme);
  try {
    window.localStorage.setItem(CACHE_KEY, theme);
  } catch {
    // Private mode, blocked site data, quota — the cache is an optimisation.
  }
}

/**
 * Replay the cached choice before the first paint. Called from main.tsx rather
 * than from a component: by the time a component's effect runs the browser has
 * already painted at least once, which is the flash this exists to prevent.
 */
export function applyCachedTheme(): void {
  let cached: string | null;
  try {
    cached = window.localStorage.getItem(CACHE_KEY);
  } catch {
    cached = null;
  }
  const parsed = themeSchema.safeParse(cached);
  applyTheme(parsed.success ? parsed.data : 'system');
}

/**
 * Keep the mobile chrome colour in step with the device while the preference is
 * 'system'. The tokens follow prefers-color-scheme on their own; the meta tag
 * is the one thing CSS cannot update, so it needs a listener. Returns the
 * unsubscribe so the caller's effect can clean up.
 */
export function watchDeviceTheme(theme: ThemePreference): () => void {
  const media = window.matchMedia('(prefers-color-scheme: dark)');
  const onChange = (): void => {
    applyTheme(theme);
  };
  media.addEventListener('change', onChange);
  return () => {
    media.removeEventListener('change', onChange);
  };
}
